import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import React, { useCallback, useEffect } from 'react';
import Dropzone from "react-dropzone";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn, formatBytes } from "@/lib/utils";
import { CrossIcon, FileExtensionWithPreview, PlusIcon, ShareIcon } from '../icons';
const FileUploader = props => {
  const {
    files,
    setFiles,
    onUpload,
    progresses,
    accept = {
      "image/*": []
    },
    maxSize = 1024 * 1024 * 2,
    maxFileCount = 1,
    multiple = false,
    disabled = false,
    className,
    ...dropzoneProps
  } = props;
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (!multiple && maxFileCount === 1 && acceptedFiles.length > 1) {
      toast.error("Cannot upload more than 1 file at a time");
      return;
    }
    if ((files?.length ?? 0) + acceptedFiles.length > maxFileCount) {
      toast.error(`Cannot upload more than ${maxFileCount} files`);
      return;
    }
    const newFiles = acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    }));
    const updatedFiles = files ? [...files, ...newFiles] : newFiles;
    setFiles(updatedFiles);
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(({
        file
      }) => {
        toast.error(`File ${file.name} was rejected`);
      });
    }
    if (onUpload && updatedFiles.length > 0 && updatedFiles.length <= maxFileCount) {
      const target = updatedFiles.length > 0 ? `${updatedFiles.length} files` : `file`;
      toast.promise(onUpload(updatedFiles), {
        loading: `Uploading ${target}...`,
        success: () => {
          setFiles([]);
          return `${target} uploaded`;
        },
        error: `Failed to upload ${target}`
      });
    }
  }, [files, maxFileCount, multiple, onUpload, setFiles]);
  function onRemove(index) {
    if (!files) return;
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  }

  // Revoke preview url when component unmounts
  useEffect(() => {
    return () => {
      if (!files) return;
      files.forEach(file => {
        if (isFileWithPreview(file)) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const filesAreSelected = !!files?.length;
  return <div>
      <Dropzone onDrop={onDrop} accept={accept} maxSize={maxSize} minSize={9} noClick={filesAreSelected} maxFiles={maxFileCount} multiple={maxFileCount > 1 || multiple} disabled={disabled}>
        {({
        getRootProps,
        getInputProps,
        isDragActive,
        open
      }) => <div {...getRootProps()} className={cn("group grid w-full bg-action-hover place-items-center rounded-lg border border-dashed border-border-default p-4 text-center transition ", "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-3xl", isDragActive && "border-neutral-300/50", !filesAreSelected && "cursor-pointer", className)} {...dropzoneProps}>
            <input {...getInputProps()} />

            {filesAreSelected ? <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {files?.map((file, index) => <FileCard key={index} file={file} onRemove={() => onRemove(index)} progress={progresses?.[file.name]} />)}
                </div>
               {files?.length < maxFileCount && <div className='flex justify-end'>
                  <Button type="button" variant="secondary" size="sm" className="mt-6 bg-base-white" onClick={open}>
                    <PlusIcon size={20} className='size-5 ms-auto' /> Add
                  </Button>
                </div>}
              </div> : <>
                <div className="flex flex-col items-center justify-center py-[74px]">
                  <Button isIcon variant='secondary' className='bg-base-white' type='button'>
                    <ShareIcon size={18} className="size-[18px]" />
                  </Button>
                  {/* TODO, improvement, use transition to animate the text of drag active */}
                  {isDragActive ? <p className='text-text-placeholder mt-4'>Drop the files here</p> : <p className='text-text-placeholder mt-4'>Drag & drop or <strong className='text-text-primary'>Choose file</strong></p>}
                </div>
              </>}

          </div>}
      </Dropzone>
      {/* TODO, feat make the supported formats list dynamic based on the accept props */}
      <p className="mt-2 text-body-xs text-text-placeholder">Supported formats: PDF, DOCS, TXT (Max size {formatBytes(maxSize)})</p>

    </div>;
};
function FileCard(t0) {
  const $ = _c(18);
  const {
    file,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== file) {
    t1 = isFileWithPreview(file) && <FilePreview file={file} />;
    $[0] = file;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== file.name) {
    t2 = <p className="text-body-xs text-left break-all line-clamp-1">{file.name}</p>;
    $[2] = file.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== file.size) {
    t3 = formatBytes(file.size);
    $[4] = file.size;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <p className="text-body-xs text-left text-text-placeholder">{t3}</p>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div>{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CrossIcon size={20} className="size-5" aria-hidden="true" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onRemove) {
    t7 = <Button onClick={onRemove} type="button" variant="ghost" size="sm" role="button" className="group-hover/file:block transition-all ease-in-out duration-300 hidden h-6 !p-0 hover:bg-base-white size-6 self-center z-[1]" isIcon={true}>{t6}</Button>;
    $[12] = onRemove;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t1 || $[15] !== t5 || $[16] !== t7) {
    t8 = <div className="grid grid-cols-[28px_1fr_0px] hover:grid-cols-[28px_1fr_24px] gap-4 bg-base-white rounded-xl p-3 group/file z-10">{t1}{t5}{t7}</div>;
    $[14] = t1;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function isFileWithPreview(file) {
  return "preview" in file && typeof file.preview === "string";
}
function FilePreview(t0) {
  const $ = _c(7);
  const {
    file
  } = t0;
  let t1;
  if ($[0] !== file.name) {
    t1 = file.name.split(".").pop() ?? "";
    $[0] = file.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const extension = t1;
  if (file.type.startsWith("image/") && isFileWithPreview(file)) {
    let t2;
    if ($[2] !== file.name || $[3] !== file.preview) {
      t2 = <Image src={file.preview} alt={file.name} width={48} height={48} loading="lazy" className="aspect-square shrink-0 rounded-md object-cover" />;
      $[2] = file.name;
      $[3] = file.preview;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== extension) {
    t2 = <><FileExtensionWithPreview extension={extension} className="select-none size-7 self-center" size={28} /></>;
    $[5] = extension;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export default FileUploader;